/* eslint-disable import/no-anonymous-default-export */
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import colors from "../../config/colors";
import { RiBankFill, RiPhoneFill } from "react-icons/ri";
import { MdEmail } from "react-icons/md";
import { IoLocationSharp, IoPersonCircle } from "react-icons/io5";
import { IoIosMail } from "react-icons/io";
import { BiPlus } from "react-icons/bi";
import { AiFillCalendar, AiFillTag } from "react-icons/ai";
import AppInput from "../../common/AppInput";
import AppButton from "../../common/AppButton";
import LabelText from "../../common/LabelText";
import validation from "../../common/validation";
import api from "../../config/api";
import focusInputByName from "../../common/focusInputByName";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import config from "../../config/config";
import LabelInput from "../../common/LabelInput";
import { FaPlusCircle, FaTimes } from "react-icons/fa";
import {
  getContDetailsByInv,
  getJobDetailsByInv,
} from "../../stores/PaymentStore";
import { useEffect } from "react";
import { getLocations } from "../../stores/LocationStore";
import addPrecision from "../../common/addPrecision";
import { getIncompleteInvoiceIds } from "../../stores/ContractInvocieStore";

export default function BulkPaymentModel() {
  const queryClient = useQueryClient();

  const Body = ({ onDone }) => {
    const [disabled, setDisabled] = useState(false);
    const [amountDisabled, setAmountDisabled] = useState(true);
    const [form, setForm] = useState({
      paymentDate: new Date(),
      list: [],
      amount: 0,
    });

    const { data: incompleteInvoiceIds } = useQuery({
      queryFn: () => getIncompleteInvoiceIds(),
      queryKey: ["invoices", "contractInvoices", "incomplete"],
    });

    const { data: job, refetch: refetchJob } = useQuery({
      queryFn: () => getJobDetailsByInv({ invoiceID: form.invNo.name }),
      queryKey: ["job", "byInv"],
      enabled: false,
    });

    const { data: contract, refetch: refetchContract } = useQuery({
      queryFn: () => getContDetailsByInv({ invoiceID: form.invNo.name }),
      queryKey: ["Contract", "byContInv"],
      enabled: false,
    });

    const { data: locations } = useQuery({
      queryFn: () => getLocations(),
      queryKey: ["locations"],
    });

    const handleChange = ({ target }) => {
      const name = target.name;
      const value = target.value;

      setForm({ ...form, [name]: value });
    };

    const onAmountblur = ({ target }) => {
      let value = target.value;

      const max = +target.max;
      if (+value > max) {
        value = max;
      }

      setForm({ ...form, amount: value });
    };

    const onRemove = (inv) => {
      const ind = form.list.findIndex((item) => item.invoiceID === inv);
      const arr = [...form.list];
      arr.splice(ind, 1);
      setForm((cur) => ({ ...cur, list: arr }));
    };

    useEffect(() => {
      if (!form.invNo) {
        setAmountDisabled(true);
        setForm((cur) => ({ ...cur, amount: 0 }));
      }

      refetchJob();
      refetchContract();
    }, [form.invNo]);

    useEffect(() => {
      if (!job?.invoiceID && !contract?.invoiceID) {
        setAmountDisabled(true);
        setForm((cur) => ({ ...cur, amount: 0 }));
        return;
      }

      if (job?.balance === 0 || contract?.balance === 0) {
        setAmountDisabled(true);
        toast.warning("Already fully paid");
        setForm((cur) => ({ ...cur, amount: 0 }));

        return;
      }

      if (form.invNo && (job?.balance || contract?.balance)) {
        setAmountDisabled(false);
        setForm((cur) => ({
          ...cur,
          amount: job?.balance || contract?.balance,
        }));
      }
    }, [job, contract]);

    const onAdd = () => {
      if (
        form.invNo &&
        form.amount &&
        (job?.invoiceID || contract?.invoiceID)
      ) {
        const alreadyExist = form.list.find(
          (item) => item.invoiceID === form.invNo.name
        );
        if (alreadyExist) return toast.warning("Already in the list");

        const locationsTitles = job?.invoiceID
          ? job.location
              ?.map((loc) => locations.find((item) => item._id === loc)?.title)
              .join(";")
          : contract.contract.location
              ?.map((loc) => locations.find((item) => item._id === loc)?.title)
              .join(";");

        const startDate = new Date(
          job?.invoiceID ? job.jobStartDate : contract.startDate
        ).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });

        const endDate = new Date(
          job?.invoiceID ? job.jobEndDate : contract.endDate
        ).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
        const jobDate = `${startDate}-${endDate}`;
        const newLine = {
          invoiceID: form.invNo.name,
          amount: Number(form.amount),
          locationsTitles,
          jobDate,
          job: job?.invoiceID ? job._id : undefined,
          contract: job?.invoiceID ? undefined : contract.contract._id,
          contractInvoice: job?.invoiceID ? undefined : contract._id,
        };

        setForm((cur) => ({
          ...cur,
          list: [...cur.list, newLine],
          invNo: "",
          amount: "",
        }));
      } else {
        return toast.warning("Missing required fields");
      }
    };

    const handleSubmit = async (event) => {
      setDisabled(true);
      event.preventDefault();
      const res = await api.post("jobPayments/multiple", {
        ...form,
      });

      if (!res.ok) {
        setDisabled(true);
        return toast.error(res.originalError.message);
      }

      queryClient.invalidateQueries(["jobPayments"], ["job", "byInv"]);

      toast.success("Job Payments successfully updated");

      setDisabled(true);
      setForm({ paymentDate: new Date() });
      onDone && onDone();
    };

    return (
      <React.Fragment>
        <Modal.Header closeButton>
          <span className="d-flex" style={{ alignItems: "center" }}>
            <b>Bulk Payment</b>
          </span>
        </Modal.Header>
        <Modal.Body style={{ overflowY: "scroll", padding: 20 }}>
          <form id="form">
            <div
              className="d-flex"
              style={{
                flexDirection: "column",
                gap: "20px",
                padding: "0px 30px 20px 30px",
                overflowY: "auto",
                height: 200,
              }}
            >
              <div
                className="d-flex"
                style={{
                  gap: 20,
                  marginTop: 10,
                  position: "absolute",
                  zIndex: 1000,
                  top: 25,
                  left: 50,
                }}
              >
                <AppInput
                  type="date"
                  label="Payment Date"
                  onChange={handleChange}
                  name="paymentDate"
                  placeholder="Payment Date"
                  value={new Date(form.paymentDate)}
                  leftIcon={
                    <AiFillCalendar
                      color={colors.silver}
                      size={config.iconSize}
                    />
                  }
                  floatLabel
                  required
                  style={{ width: 300 }}
                />
                <AppInput
                  onChange={handleChange}
                  label="Check Number"
                  placeholder="Check Number"
                  name="checkNo"
                  value={form?.checkNo || ""}
                  required
                  floatLabel
                  style={{ width: 300 }}
                />
              </div>
              <div
                className="d-flex"
                style={{
                  gap: 20,
                  marginTop: 10,
                  justifyContent: "start",
                  alignItems: "center",
                  position: "absolute",
                  zIndex: 1000,
                  top: 120,
                  left: 50,
                }}
              >
                <AppInput
                  type="dropdown"
                  placeholder="Invoice No"
                  name="invNo"
                  chip
                  onChange={handleChange}
                  style={{ width: 300 }}
                  options={incompleteInvoiceIds
                    ?.filter((i) => i !== undefined)
                    ?.map((i) => {
                      return {
                        name: i,
                        title: i,
                      };
                    })}
                  filterBy="name"
                  value={form?.invNo}
                  required
                />
                <AppInput
                  onChange={handleChange}
                  onBlur={onAmountblur}
                  label="Amount"
                  placeholder="Amount"
                  name="amount"
                  disabled={amountDisabled}
                  value={form?.amount || ""}
                  max={job?.balance || contract?.balance || 0}
                  floatLabel
                  style={{ width: 300 }}
                />
                <FaPlusCircle
                  size={50}
                  onClick={onAdd}
                  style={{ cursor: "pointer" }}
                />
              </div>
            </div>
          </form>
          <div
            style={{
              height: 200,
              overflow: "auto",
              border: "1px solid #000",
              borderRadius: 5,
              padding: 10,
            }}
          >
            <table style={{ width: "100%" }}>
              <tbody>
                {form.list.map((item) => (
                  <tr key={item.invoiceID}>
                    <td>{item.invoiceID}</td>
                    <td style={{ maxWidth: 150 }}>{item.locationsTitles}</td>
                    <td>${addPrecision(item.amount)}</td>
                    <td>{item.jobDate}</td>
                    <td>
                      <FaTimes
                        size={25}
                        color="red"
                        style={{ cursor: "pointer" }}
                        onClick={() => onRemove(item.invoiceID)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <AppButton
            disabled={disabled || form.list.length === 0}
            text="Receive"
            form="form"
            type="submit"
            className="text-primary btn-light"
            onClick={handleSubmit}
          />
        </Modal.Footer>
      </React.Fragment>
    );
  };

  return { Body };
}
